import type { UploadFile } from '@edwin-group/people-graphql';
import { ReactNode } from 'react';
// import DefaultContentTemplateTags from '@/components/DefaultContentTemplate/Tags';
import { Link } from 'react-router-dom';
import { useResponsiveImage } from '@/utils/useResponsiveImage';
import classNames from 'classnames';
import { CustomImage } from '@/components/CustomImage';

type LessonCardProps = {
  title: string;
  description: string;
  isLocked?: boolean;
  slug?: string;
  thumbnail?: UploadFile;
  url: string;
  done?: boolean;
  lockedLabel?: string;
  watchAgainLabel?: string;
  watchLabel?: string;
  progress?: number;
  descriptionClasses?: string;
  button?: ReactNode;
  favorite?: ReactNode;
  tag?: string;
  duration?: string;
  onClick?: () => void;
  truncateDescription?: boolean;
  onClickButton?: () => void;
  noImage?: boolean;
};

const LessonCard = ({
  title,
  description,
  thumbnail,
  noImage = false,
  url,
  progress,
  button,
  favorite,
  descriptionClasses,
  // tag,
  duration,
  onClick,
  truncateDescription = true,
  onClickButton,
  watchLabel,
  watchAgainLabel,
  done,
}: LessonCardProps) => {
  const { src } = useResponsiveImage(thumbnail);

  return (
    <div className="flex flex-col gap-sh-16 rounded-sh-16 border border-teal-accent bg-light-green p-sh-16 md:flex-row md:gap-sh-25">
      {thumbnail && !noImage && (
        <div className=" w-full shrink-0 overflow-hidden rounded-t-sh-16 bg-people-green-200 md:w-[359px] md:rounded-sh-16">
          <CustomImage
            src={src}
            alt={thumbnail.alternativeText || ''}
            className={classNames('h-full w-full bg-cover bg-center', onClick ? 'cursor-pointer' : '')}
            onClick={onClick}
          />
        </div>
      )}
      <div className="flex flex-1 flex-col gap-sh-16 pt-0 md:pt-sh-16">
        <div className="flex items-start justify-between gap-5">
          <h2 className="flex-1 font-haffer text-sh-20-30 font-bold text-black">{title}</h2>
          {favorite}
        </div>
        {duration ? (
          <div className="flex items-center gap-1 font-haffer text-sh-xxs text-dark-grey">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <g clipPath="url(#clip0_24721_1486)">
                <path
                  d="M8 4V8L10.6667 9.33333"
                  stroke="#135959"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.99967 14.6663C11.6816 14.6663 14.6663 11.6816 14.6663 7.99967C14.6663 4.31778 11.6816 1.33301 7.99967 1.33301C4.31778 1.33301 1.33301 4.31778 1.33301 7.99967C1.33301 11.6816 4.31778 14.6663 7.99967 14.6663Z"
                  stroke="#135959"
                  strokeWidth="1.33333"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_24721_1486">
                  <rect width="16" height="16" fill="white" />
                </clipPath>
              </defs>
            </svg>
            <span>{duration}</span>
          </div>
        ) : null}
        <p
          className={classNames(
            'font-haffersq text-sh-18-28 text-black',
            descriptionClasses || '',
            truncateDescription ? 'line-clamp-2' : '',
          )}
          title={description}>
          {description}
        </p>
        {progress && progress > 0 ? (
          <div className="h-[6px] w-full rounded-full bg-light-green">
            <div
              className="h-[6px] rounded-full bg-people-green"
              style={{ width: `${progress <= 100 ? progress : 100}%` }}></div>
          </div>
        ) : null}
        <div>
          {!button ? (
            <Link
              to={url}
              className="font-haffer text-sh-12-18 font-bold text-dark-green"
              onClick={() => {
                if (onClickButton) {
                  onClickButton();
                }
              }}>
              {done ? watchAgainLabel || watchLabel || 'Learn more' : watchLabel || 'Learn more'}
            </Link>
          ) : (
            button
          )}
        </div>
      </div>
    </div>
  );
};

export default LessonCard;
